import React, { useState, useEffect, useCallback } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogClose,
} from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { Check, X, Globe, Loader2, RefreshCw } from "lucide-react";
import externalAPI from "../../Connect/ExternalAPI";
import { toast } from "sonner";
import { cn } from "../../Lib/Cn";

const SETUP_CRITERIA = [
	{
		id: "bizType",
		label: "Business Type",
		check: (profile) => Boolean(profile?.BizTypes?.trim()),
	},
	{
		id: "industrySegment",
		label: "Industry & Segment",
		check: (profile) => Boolean(profile?.SegmentID || profile?.Segment?.trim()),
	},
	{
		id: "logo",
		label: "Logo",
		check: (profile) => Boolean(profile?.Logo?.trim()),
	},
	{
		id: "tagline",
		label: "Tagline",
		check: (profile) => Boolean(profile?.Tagline?.trim()),
	},
	{
		id: "about",
		label: "About",
		check: (profile) => Boolean(profile?.Description?.trim()),
	},
	{
		id: "location",
		label: "Location",
		check: (profile) => Boolean(profile?.City?.trim() || profile?.State?.trim()),
	},
	{
		id: "email",
		label: "Email",
		check: (profile) => Boolean(profile?.Email?.trim()),
	},
	{
		id: "locationMap",
		label: "Location Link",
		check: (profile) => Boolean(profile?.LocLink?.trim()),
	},
	{
		id: "catalog",
		label: "Product Catalog",
		check: (_, catalog) => catalog?.length > 0,
	},
];

export default function WebstoreSetupProgressModal({ agency, open, onClose }) {
	const [loading, setLoading] = useState(false);
	const [checklist, setChecklist] = useState(null);
	const [error, setError] = useState(null);

	const agencyID = agency?.agencyID || agency?.AgencyID;

	const fetchWebstoreDetails = useCallback(async () => {
		if (!agencyID) return;
		setLoading(true);
		setError(null);

		try {
			const res = await externalAPI.getAgencyVitals(agencyID);
			const pkg =
				typeof res?.updatePackage === "string"
					? JSON.parse(res.updatePackage)
					: res?.updatePackage;

			const profile = pkg?.Table1?.[0] || null;
			let catalog = [];
			try {
				catalog = profile?.Catalog ? JSON.parse(profile.Catalog) : [];
			} catch {
				catalog = [];
			}

			const activeCatalog = Array.isArray(catalog)
				? catalog.filter((item) => !item?.IsD)
				: [];

			// Evaluate criteria and sort unfilled items first
			const evaluated = SETUP_CRITERIA.map((criterion) => ({
				id: criterion.id,
				label: criterion.label,
				filled: Boolean(criterion.check(profile, activeCatalog)),
			})).sort((a, b) => (a.filled === b.filled ? 0 : a.filled ? 1 : -1));

			setChecklist(evaluated);
		} catch (err) {
			console.error("Failed to load webstore details:", err);
			setError(err.message || "Failed to load webstore setup data");
			toast.error("Failed to load webstore details");
		} finally {
			setLoading(false);
		}
	}, [agencyID]);

	useEffect(() => {
		if (open && agencyID) {
			fetchWebstoreDetails();
		} else if (!open) {
			setChecklist(null);
			setError(null);
		}
	}, [open, agencyID, fetchWebstoreDetails]);

	const handleOpenWebstore = () => {
		if (agencyID) {
			window.open(
				`${window.location.origin}/web/store?m=${agencyID}`,
				"_blank",
				"noopener,noreferrer"
			);
		}
	};

	if (!open) return null;

	const completedCount = checklist?.filter((item) => item.filled).length || 0;
	const progressPercent = checklist?.length
		? Math.round((completedCount / checklist.length) * 100)
		: 0;
	const title = checklist ? `Webstore Progress - ${progressPercent}%` : "Webstore Progress";

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent
				onInteractOutside={(e) => e.preventDefault()}
				onOpenAutoFocus={(e) => e.preventDefault()}
				className="max-w-full sm:max-w-lg flex flex-col p-0 overflow-hidden"
			>
				<DialogHeader className="gap-0 px-4">
					<div className="flex items-center justify-between w-full">
						<DialogTitle className="text-lg md:text-xl flex items-center gap-3">
							<span className="truncate">{title}</span>
						</DialogTitle>
						<DialogClose
							className="cursor-pointer p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-black transition-colors"
							tabIndex={-1}
							onClick={onClose}
						>
							<X className="h-5 w-5" />
							<span className="sr-only">Close</span>
						</DialogClose>
					</div>
				</DialogHeader>

				<div className="px-4 flex-1 overflow-y-auto max-h-[75vh]">
					{loading ? (
						<div className="py-14 flex flex-col items-center justify-center gap-3">
							<Loader2 className="w-8 h-8 animate-spin text-sahi-primary-color" />
							<span className="text-sm font-medium text-sub-text-color">
								Fetching webstore setup details...
							</span>
						</div>
					) : error ? (
						<div className="py-10 px-4 text-center space-y-3">
							<p className="text-sm text-red-600 font-medium">{error}</p>
							<Button
								variant="outline"
								size="sm"
								onClick={fetchWebstoreDetails}
								className="inline-flex items-center gap-1.5"
							>
								<RefreshCw className="w-4 h-4" />
								Retry
							</Button>
						</div>
					) : (
						<div className="pt-3">
							<div className="grid grid-cols-2 gap-3">
								{checklist?.map((item) => (
									<div
										key={item.id}
										className={cn(
											"flex items-center p-3 rounded-lg border transition-colors gap-3",
											item.filled
												? "border-gray-200 bg-white hover:bg-zinc-50"
												: "border-gray-200 bg-zinc-50/50 hover:bg-zinc-50"
										)}
									>
										<span
											className={cn(
												"flex items-center justify-center shrink-0",
												item.filled ? "text-sahi-green" : "text-zinc-400"
											)}
										>
											{item.filled ? (
												<Check className="h-5 w-5" strokeWidth={2.5} />
											) : (
												<X className="h-5 w-5" strokeWidth={2} />
											)}
										</span>
										<span
											className="text-sm font-medium text-text-color truncate"
											title={item.label}
										>
											{item.label}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					<div className="py-4 mt-2 border-t border-gray-100 flex gap-3">
						<Button onClick={handleOpenWebstore} className="w-full sm:w-auto">
							<Globe className="h-4 w-4" />
							Open Webstore
						</Button>
						<Button
							type="outline"
							onClick={onClose}
							className="sahi-secondary-button w-full sm:w-auto"
						>
							<X className="h-4 w-4" strokeWidth={3} />
							Close
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
